import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';

import AssetV2MaintenanceTabDisplay from './AssetV2MaintenanceTabDisplay';
import { DUE, UPCOMING } from '../../Constants/GatewayReminderInstances';
import {
  ALL_TYPE,
  CALENDAR_TYPE,
  SIGNAL_TYPE,
} from '../../Constants/GtemplateReminder';
import useDebounce from '../../Hooks/useDebounce';
import {
  generateCalendarTypeProps,
  generateSignalTypeProps,
} from '../../Lib/GatewayReminderInstance';
import { generateSignalUnitMap } from '../../Lib/Gtemplate';
import GatewayReminderInstanceService from '../../Services/GatewayReminderInstanceService';

export default memo(AssetV2MaintenanceTabContainer);

const initState = {
    checkedInstances: new Map(),
    count: 0,
    due: 0,
    dueInstances: new Map(),
    error: '',
    gateway_id: '',
    gateway_reminder_instance_id: '',
    instances: [],
    instancesLoading: true,
    page: 1,
    reminder_name: '',
    showCompleteMaintenanceModal: false,
    showMobileFilters: false,
    showUpdateServiceValuesModal: false,
    upcoming: 0,
    upcomingInstances: new Map(),
  },
  limit = 20;

function AssetV2MaintenanceTabContainer(props) {
  const { _id: gateway_id, gtemplate, shadow } = props.asset,
    api = useMemo(() => new GatewayReminderInstanceService(), []),
    [state, updateState] = useState(initState),
    pages = useMemo(() => Math.ceil(state.count / limit), [state.count]),
    debouncedReminderName = useDebounce(state.reminder_name, 1000),
    signalUnitMap = generateSignalUnitMap(gtemplate);

  const shadowValueBySignalIdMap = useMemo(
    () =>
      shadow?.signalCollections?.reduce?.((map, col) => {
        if (!Array.isArray(col?.signals) || !col?.signals?.length) {
          return map;
        }

        return col.signals.reduce((acc, s) => acc.set(s._id, s.value), map);
      }, new Map()) ?? new Map(),
    [shadow]
  );

  const instanceShadowValueMap = useMemo(() => {
    const now = Date.now();
    return (
      state.instances?.reduce?.((map, instance) => {
        switch (instance.GtemplateReminder.type) {
          case ALL_TYPE: {
            const calendarProps = generateCalendarTypeProps(instance, now);
            const signalProps = generateSignalTypeProps(
              instance,
              shadowValueBySignalIdMap
            );
            return map.set(instance._id, { ...calendarProps, ...signalProps });
          }
          case CALENDAR_TYPE:
            return map.set(
              instance._id,
              generateCalendarTypeProps(instance, now)
            );
          case SIGNAL_TYPE:
            return map.set(
              instance._id,
              generateSignalTypeProps(instance, shadowValueBySignalIdMap)
            );
          default:
            break;
        }
        return map;
      }, new Map()) ?? new Map()
    );
  }, [shadowValueBySignalIdMap, state.instances]);

  const handleClearFiltersClick = useCallback(() => {
    updateState((prevState) => ({ ...prevState, reminder_name: '' }));
  }, []);

  const handleCompleteMaintenanceSuccess = async () => {
    try {
      props.showCompleteMaintenanceSuccessAlert();
      updateState(initState);
      const params = {
        gateway_id,
        limit,
        page: state.page,
        reminder_name: debouncedReminderName,
      };
      const { count, data, due, page, upcoming } =
        await api.getPaginatedGatewayReminderInstances(params);
      const dueAndUpcomingInstances = data.reduce(
        (acc, instance) => {
          if (!instance?.MaintenanceNotifications?.length) return acc;
          switch (instance?.MaintenanceNotifications?.[0]?.status) {
            case DUE:
              acc.dueInstances.set(instance._id, instance);
              return acc;
            case UPCOMING:
              acc.upcomingInstances.set(instance._id, instance);
              return acc;
            default:
              return acc;
          }
        },
        { dueInstances: new Map(), upcomingInstances: new Map() }
      );
      updateState((prevState) => ({
        ...prevState,
        ...dueAndUpcomingInstances,
        count,
        due,
        instances: data,
        instancesLoading: false,
        page,
        upcoming,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        instancesLoading: false,
      }));
    }
  };

  const handleUpdateServiceValuesSuccess = async () => {
    try {
      props.showUpdateMaintenanceServiceValuesSuccessAlert();
      updateState(initState);
      const params = {
        gateway_id,
        limit,
        page: state.page,
        reminder_name: debouncedReminderName,
      };
      const { count, data, due, page, upcoming } =
        await api.getPaginatedGatewayReminderInstances(params);
      const dueAndUpcomingInstances = data.reduce(
        (acc, instance) => {
          if (!instance?.MaintenanceNotifications?.length) return acc;
          switch (instance?.MaintenanceNotifications?.[0]?.status) {
            case DUE:
              acc.dueInstances.set(instance._id, instance);
              return acc;
            case UPCOMING:
              acc.upcomingInstances.set(instance._id, instance);
              return acc;
            default:
              return acc;
          }
        },
        { dueInstances: new Map(), upcomingInstances: new Map() }
      );
      updateState((prevState) => ({
        ...prevState,
        ...dueAndUpcomingInstances,
        count,
        due,
        instances: data,
        instancesLoading: false,
        page,
        upcoming,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        instancesLoading: false,
      }));
    }
  };

  const handleInstanceChecked = useCallback(([id, instance]) => {
    updateState((prevState) => {
      const checkedInstances = new Map(prevState.checkedInstances);
      if (checkedInstances.has(id)) {
        checkedInstances.delete(id);
      } else {
        checkedInstances.set(id, instance);
      }

      return { ...prevState, checkedInstances };
    });
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      if (page === state.page) return;
      updateState((prevState) => ({ ...prevState, page }));
    },
    [state.page]
  );

  const handleSelectAllInstances = useCallback(() => {
    updateState((prevState) => {
      const allChecked =
        prevState.checkedInstances.size === prevState.instances.length;
      const checkedInstances = allChecked
        ? new Map()
        : new Map(
            prevState.instances.map((instance) => [instance._id, instance])
          );
      return { ...prevState, checkedInstances };
    });
  }, []);

  const handleStatePropUpdate = useCallback(({ target: { name, value } }) => {
    updateState((prevState) => ({ ...prevState, [name]: value }));
  }, []);

  const toggleCompleteMaintenanceModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showCompleteMaintenanceModal: !prevState.showCompleteMaintenanceModal,
      }));
    }, []),
    toggleHistoryModal = useCallback(
      ({ gateway_id = '', gateway_reminder_instance_id = '' } = {}) => {
        updateState((prevState) => ({
          ...prevState,
          gateway_id,
          gateway_reminder_instance_id,
        }));
      },
      []
    ),
    toggleMobileFilters = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showMobileFilters: !prevState.showMobileFilters,
      }));
    }, []),
    toggleUpdateServiceValuesModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showUpdateServiceValuesModal: !prevState.showUpdateServiceValuesModal,
      }));
    }, []);

  useEffect(() => {
    updateState((prevState) => ({
      ...prevState,
      instances: [],
      instancesLoading: true,
    }));
    const source = CancelToken.source();
    async function fetchGatewayInstances() {
      try {
        const params = {
          gateway_id,
          limit,
          page: state.page,
          reminder_name: debouncedReminderName,
        };
        const { count, data, due, page, upcoming } =
          await api.getPaginatedGatewayReminderInstances(params);
        const dueAndUpcomingInstances = data.reduce(
          (acc, instance) => {
            if (!instance?.MaintenanceNotifications?.length) return acc;
            switch (instance?.MaintenanceNotifications?.[0]?.status) {
              case DUE:
                acc.dueInstances.set(instance._id, instance);
                return acc;
              case UPCOMING:
                acc.upcomingInstances.set(instance._id, instance);
                return acc;
              default:
                return acc;
            }
          },
          { dueInstances: new Map(), upcomingInstances: new Map() }
        );
        updateState((prevState) => ({
          ...prevState,
          ...dueAndUpcomingInstances,
          count,
          due,
          instances: data,
          instancesLoading: false,
          page,
          showMobileFilters: false,
          upcoming,
        }));
      } catch (err) {
        if (isCancel(err)) return;
        const error = err.response?.data?.message ?? err.message;
        updateState((prevState) => ({
          ...prevState,
          error,
          instancesLoading: false,
          showMobileFilters: false,
        }));
      }
    }

    fetchGatewayInstances();
    return () => {
      source.cancel();
    };
  }, [api, debouncedReminderName, gateway_id, state.page]);
  return (
    <AssetV2MaintenanceTabDisplay
      {...state}
      assetNickname={props.asset?.nickname}
      handleClearFiltersClick={handleClearFiltersClick}
      handleCompleteMaintenanceSuccess={handleCompleteMaintenanceSuccess}
      handleInstanceChecked={handleInstanceChecked}
      handlePageChange={handlePageChange}
      handleSelectAllInstances={handleSelectAllInstances}
      handleStatePropUpdate={handleStatePropUpdate}
      handleUpdateServiceValuesSuccess={handleUpdateServiceValuesSuccess}
      instanceShadowValueMap={instanceShadowValueMap}
      orgID={props.asset?.orgID}
      pages={pages}
      shadowValueBySignalIdMap={shadowValueBySignalIdMap}
      signalUnitMap={signalUnitMap}
      toggleCompleteMaintenanceModal={toggleCompleteMaintenanceModal}
      toggleHistoryModal={toggleHistoryModal}
      toggleMobileFilters={toggleMobileFilters}
      toggleUpdateServiceValuesModal={toggleUpdateServiceValuesModal}
    />
  );
}

AssetV2MaintenanceTabContainer.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    gtemplate: PropTypes.shape({ sharedGtemplate: PropTypes.object }),
    nickname: PropTypes.string,
    orgID: PropTypes.string,
    shadow: PropTypes.shape({
      signalCollections: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          signals: PropTypes.arrayOf(
            PropTypes.shape({
              _id: PropTypes.string,
              value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            })
          ),
        })
      ),
    }),
  }),
  showCompleteMaintenanceSuccessAlert: PropTypes.func,
  showUpdateMaintenanceServiceValuesSuccessAlert: PropTypes.func,
};
